/**
 * Moiré patterns: two simple line sets laid over each other, so that a third,
 * larger pattern appears between them. Each pattern is one small WebGL2 pass,
 * the same kind as the echo rings (echoRings.ts), and they share its rules:
 * colors are fixed per host, lines are antialiased by their own derivatives,
 * and without WebGL2 the host is simply left on its own background.
 *
 *   stations  two ring sets from two points, XOR. Points that meet merge into one ring set.
 *   echo      the mark's echo lines under a tilted ruling. Needs `mark`.
 *   typeball  two fans of spokes, a few pixels apart, turning opposite ways.
 *   platen    two rulings a couple of degrees apart; feeding one moves the fringes far.
 *   ribbon    two dot screens a few degrees apart: rosettes.
 *   swell     two wavy rulings of slightly different wavelength, XOR.
 *
 * Colors may be any CSS color, including translucent ones, so a pattern can sit
 * on a themed surface with a transparent ground.
 *
 * Ported to the website as superelectric-site/src/scripts/moire.ts; the two
 * are separate packages. Keep them in step.
 */
import { MARK_PATH } from '../constants/brandMark'

export type MoirePattern = 'stations' | 'echo' | 'typeball' | 'platen' | 'ribbon' | 'swell'

export type MoireParams = Record<string, number>

interface MoireColors {
  ground: string
  line: string
  accent?: string
}

export interface MoireOptions {
  /** Element the canvas covers. Must be positioned. */
  host: HTMLElement
  canvas: HTMLCanvasElement
  pattern: MoirePattern
  colors: MoireColors
  /** Line period in CSS px. Each pattern has its own default. */
  step?: number
  /** Pattern parameters; see DEFAULTS. */
  params?: MoireParams
  /** Run the pattern's slow drift. Ignored when the user prefers reduced motion. */
  loop?: boolean
  /** Where the pattern is centered, in host px. Defaults to the host's center. Stations may give both points. */
  origin?: (host: DOMRect) => { p1?: [number, number]; p2?: [number, number] }
  /** A rounded box kept clear of the pattern (a title block, a window). */
  clear?: HTMLElement | null
  clearRadius?: number
  /** Clear space around that box, in CSS px. */
  clearGap?: number
  /** Distance over which the pattern fades in beyond that space, in CSS px. */
  clearFade?: number
  /** The <svg> that draws the mark (216-unit grid), for the echo pattern. */
  mark?: Element | null
  /** Time, in seconds, of the still frame shown when not looping. */
  still?: number
}

export interface MoireController {
  /** Change parameters, easing over `ms` (instant with reduced motion). */
  set(params: MoireParams, ms?: number): void
  /** Start or stop the slow drift. */
  setLoop(loop: boolean): void
  /** Change colors (for a theme change). */
  setColors(colors: MoireColors): void
  /** Re-measure the host, the clear box and the origin. */
  refresh(): void
  dispose(): void
}

const DEFAULTS: Record<MoirePattern, { step: number; params: MoireParams }> = {
  // separation: 0 = merged, 1 = far apart (a fraction of the host's width). breathe: drift while looping.
  stations: { step: 8, params: { separation: 0.5, breathe: 0, dot: 4.5 } },
  // tilt in degrees.
  echo: { step: 10, params: { tilt: 1.2 } },
  // offset and hub as fractions of the host's smaller side; speed in radians per second.
  typeball: { step: 0, params: { spokes: 72, offset: 0.035, hub: 0.07, speed: 0.05 } },
  // angle in degrees, feed in px (one step is one full fringe).
  platen: { step: 6, params: { angle: 2.4, feed: 0 } },
  // angle in degrees.
  ribbon: { step: 9, params: { angle: 4 } },
  // amplitude as a fraction of the host's height, wavelength as a fraction of its width.
  swell: { step: 7, params: { amplitude: 0.2, wavelength: 0.16 } },
}

const PRE = `#version 300 es
precision highp float;
uniform vec2 u_res,u_p1,u_p2;uniform float u_dpr,u_step,u_clearR,u_clearFade;
uniform vec4 u_g,u_l,u_a,u_k,u_clear,u_field;uniform sampler2D u_sdf;
out vec4 o;
float stripe(float d,float p,float w){float u=d/p;float a=max(fwidth(u),1e-4);float t=abs(fract(u+.5)-.5);
  float s=1.-smoothstep(w*.5-a*.5,w*.5+a*.5,t);return mix(s,w,smoothstep(.35,.75,a));}
mat2 rot(float a){float c=cos(a),s=sin(a);return mat2(c,s,-s,c);}
float sdBox(vec2 p,vec4 b,float r){vec2 q=abs(p-b.xy-b.zw*.5)-b.zw*.5+r;return length(max(q,0.))+min(max(q.x,q.y),0.)-r;}
`

const MAIN = `void main(){
  vec2 p=vec2(gl_FragCoord.x,u_res.y*u_dpr-gl_FragCoord.y)/u_dpr;
  vec4 c=pat(p);
  if(u_clear.z>0.)c=mix(u_g,c,smoothstep(-.5,.5+u_clearFade,sdBox(p,u_clear,u_clearR)));
  o=c;}`

const FRAG: Record<MoirePattern, string> = {
  stations: `
vec4 pat(vec2 p){
  float a=stripe(length(p-u_p1),u_step,.5),b=stripe(length(p-u_p2),u_step,.5);
  float x=mix(a+b-2.*a*b,a,smoothstep(2.5,.2,distance(u_p1,u_p2)));
  vec4 c=mix(u_g,u_l,x);
  float r=u_k.x;
  if(r>0.)c=mix(c,u_a,max(1.-smoothstep(r-.5,r+.5,length(p-u_p1)),1.-smoothstep(r-.5,r+.5,length(p-u_p2))));
  return c;}`,
  echo: `
vec4 pat(vec2 p){
  float d=u_field.z>0.?texture(u_sdf,(p-u_field.xy)/u_field.zw).r:1e4;
  float e=stripe(d-.75*u_step,u_step,.32)*smoothstep(.55*u_step-.5,.55*u_step+.5,d);
  float th=radians(u_k.x);
  float r=stripe(dot(p-u_p1,vec2(-sin(th),cos(th))),u_step,.32);
  return mix(u_g,u_l,1.-(1.-e)*(1.-r));}`,
  typeball: `
float sp(vec2 q,float r0){float r=length(q);float u=(atan(q.y,q.x)+r0)*u_k.x/6.2831853;
  float aa=u_k.x/(6.2831853*max(r,.5));float t=abs(fract(u+.5)-.5);
  float s=1.-smoothstep(.25-aa*.5,.25+aa*.5,t);return mix(s,.5,smoothstep(.35,.75,aa));}
vec4 pat(vec2 p){
  vec2 off=vec2(u_k.y,0.);
  float a=sp(p-u_p1-off,u_k.w),b=sp(p-u_p1+off,-u_k.w);
  vec4 c=mix(u_g,u_l,a+b-2.*a*b);
  if(u_k.z>0.)c=mix(c,u_a,1.-smoothstep(u_k.z-.5,u_k.z+.5,length(p-u_p1)));
  return c;}`,
  platen: `
vec4 pat(vec2 p){
  vec2 q=p-u_p1;float th=radians(u_k.x);
  float a=stripe(q.y,u_step,.45);
  float b=stripe(dot(q,vec2(-sin(th),cos(th)))+u_k.y,u_step,.45);
  return mix(u_g,u_l,1.-(1.-a)*(1.-b));}`,
  ribbon: `
float dots(vec2 p,float S,float r){vec2 q=(fract(p/S)-.5)*S;float d=length(q);float aa=max(fwidth(d),.6);
  return 1.-smoothstep(r-aa*.5,r+aa*.5,d);}
vec4 pat(vec2 p){
  vec2 q=p-u_p1;
  float b=dots(rot(radians(u_k.x))*q,u_step,u_step*.3);
  float a=dots(q,u_step,u_step*.3);
  return mix(mix(u_g,u_a,b),u_l,a);}`,
  swell: `
vec4 pat(vec2 p){
  vec2 q=p-u_p1;
  float a=stripe(q.y+u_k.z*sin(q.x/u_k.w+u_k.x),u_step,.5);
  float b=stripe(q.y+u_k.z*sin(q.x/(u_k.w*1.13)+u_k.y+1.)+u_p2.x,u_step,.5);
  return mix(u_g,u_l,a+b-2.*a*b);}`,
}

let colorCtx: CanvasRenderingContext2D | null = null
/** Any CSS color as premultiplied RGBA in 0–1. */
function rgba(css: string | undefined): number[] {
  if (!css) return [0, 0, 0, 0]
  colorCtx ??= document.createElement('canvas').getContext('2d', { willReadFrequently: true })
  if (!colorCtx) return [0, 0, 0, 1]
  colorCtx.canvas.width = colorCtx.canvas.height = 1
  colorCtx.clearRect(0, 0, 1, 1)
  colorCtx.fillStyle = '#000'
  colorCtx.fillStyle = css
  colorCtx.fillRect(0, 0, 1, 1)
  const [r, g, b, a] = colorCtx.getImageData(0, 0, 1, 1).data
  const al = a / 255
  return [(r / 255) * al, (g / 255) * al, (b / 255) * al, al]
}

// Exact squared Euclidean distance transform (Felzenszwalb & Huttenlocher), in place.
function edt(g: Float64Array, w: number, h: number) {
  const m = Math.max(w, h)
  const f = new Float64Array(m), d = new Float64Array(m), v = new Int32Array(m), z = new Float64Array(m + 1)
  const pass = (n: number) => {
    let k = 0, r: number, s: number
    v[0] = 0; z[0] = -Infinity; z[1] = Infinity
    for (let q = 1; q < n; q++) {
      r = v[k]; s = (f[q] + q * q - f[r] - r * r) / (2 * q - 2 * r)
      while (s <= z[k]) { k--; r = v[k]; s = (f[q] + q * q - f[r] - r * r) / (2 * q - 2 * r) }
      k++; v[k] = q; z[k] = s; z[k + 1] = Infinity
    }
    for (let q = 0, j = 0; q < n; q++) {
      while (z[j + 1] < q) j++
      r = v[j]; d[q] = (q - r) * (q - r) + f[r]
    }
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = g[y * w + x]
    pass(h)
    for (let y = 0; y < h; y++) g[y * w + x] = d[y]
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = g[y * w + x]
    pass(w)
    for (let x = 0; x < w; x++) g[y * w + x] = d[x]
  }
}

let markPath: Path2D | null = null

export function mountMoire(opts: MoireOptions): MoireController | null {
  const { host, canvas: cv, pattern } = opts
  const gl = cv.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: false })
  if (!gl) return null
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
  const step = opts.step ?? DEFAULTS[pattern].step
  const params: MoireParams = { ...DEFAULTS[pattern].params, ...opts.params }

  const shader = (type: number, src: string) => {
    const s = gl.createShader(type)!
    gl.shaderSource(s, src)
    gl.compileShader(s)
    return s
  }
  const prog = gl.createProgram()!
  gl.attachShader(prog, shader(gl.VERTEX_SHADER,
    '#version 300 es\nvoid main(){gl_Position=vec4(gl_VertexID==1?3.:-1.,gl_VertexID==2?3.:-1.,0.,1.);}'))
  gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, PRE + FRAG[pattern] + MAIN))
  gl.linkProgram(prog)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null
  gl.useProgram(prog)
  const U: Record<string, WebGLUniformLocation | null> = {}
  for (const n of ['res', 'p1', 'p2', 'dpr', 'step', 'clearR', 'clearFade', 'g', 'l', 'a', 'k', 'clear', 'field', 'sdf']) U[n] = gl.getUniformLocation(prog, 'u_' + n)
  gl.uniform1i(U.sdf, 0)

  const setColors = (c: MoireColors) => {
    gl.uniform4fv(U.g, rgba(c.ground))
    gl.uniform4fv(U.l, rgba(c.line))
    gl.uniform4fv(U.a, rgba(c.accent ?? c.line))
  }
  setColors(opts.colors)

  if (pattern === 'echo') {
    gl.bindTexture(gl.TEXTURE_2D, gl.createTexture())
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  }

  let w = 0, h = 0, center: [number, number] = [0, 0], p2: [number, number] | null = null
  let t = opts.still ?? 4
  let loop = !!opts.loop && !reduced
  let visible = true
  let disposed = false
  let raf = 0
  let last = 0
  // Running tweens: name → [from, to, start, duration].
  const tweens = new Map<string, [number, number, number, number]>()

  // The echo lines need the mark's distance field: the host plus any part of the mark hanging off it.
  const buildField = (scale: number) => {
    const mark = opts.mark
    if (pattern !== 'echo' || !mark) return
    const R = host.getBoundingClientRect(), Q = mark.getBoundingClientRect()
    if (!Q.width) return
    // In layout px, relative to the host.
    const H = { left: 0, top: 0, width: R.width / scale, height: R.height / scale }
    const M = { left: (Q.left - R.left) / scale, top: (Q.top - R.top) / scale, width: Q.width / scale, height: Q.height / scale }
    const fx = Math.min(0, M.left - H.left), fy = Math.min(0, M.top - H.top)
    const fw = Math.max(H.width, M.left + M.width) - fx, fh = Math.max(H.height, M.top + M.height) - fy
    const k = Math.min(1, Math.sqrt(4e5 / (fw * fh))), fwPx = Math.ceil(fw * k), fhPx = Math.ceil(fh * k)
    const c = document.createElement('canvas')
    c.width = fwPx; c.height = fhPx
    const x = c.getContext('2d', { willReadFrequently: true })!, sc = M.width / 216
    markPath ??= new Path2D(MARK_PATH)
    x.setTransform(sc * k, 0, 0, sc * k, (M.left - H.left - fx) * k, (M.top - H.top - fy) * k)
    x.fill(markPath, 'evenodd')
    const px = x.getImageData(0, 0, fwPx, fhPx).data, n = fwPx * fhPx, g = new Float64Array(n), out = new Float32Array(n)
    for (let i = 0; i < n; i++) g[i] = px[i * 4 + 3] > 127 ? 0 : 1e20
    edt(g, fwPx, fhPx)
    for (let i = 0; i < n; i++) out[i] = Math.sqrt(g[i]) / k
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R16F, fwPx, fhPx, 0, gl.RED, gl.FLOAT, out)
    gl.uniform4f(U.field, fx, fy, fw, fh)
  }

  const layout = () => {
    if (disposed) return
    const H = host.getBoundingClientRect()
    if (!H.width || !H.height) return
    // Layout size, not the on-screen box: a modal may still be scaling in. Every
    // measured offset below is divided by the same scale.
    const scale = host.offsetWidth ? H.width / host.offsetWidth : 1
    const dpr = Math.min(devicePixelRatio || 1, 2)
    w = H.width / scale; h = H.height / scale
    cv.width = Math.round(w * dpr)
    cv.height = Math.round(h * dpr)
    gl.viewport(0, 0, cv.width, cv.height)
    gl.uniform2f(U.res, w, h)
    gl.uniform1f(U.dpr, cv.width / w)
    gl.uniform1f(U.step, step)
    const o = opts.origin?.(H)
    const unscale = (pt?: [number, number]): [number, number] | null => (pt ? [pt[0] / scale, pt[1] / scale] : null)
    center = unscale(o?.p1) ?? [w / 2, h / 2]
    p2 = unscale(o?.p2)
    const B = opts.clear?.getBoundingClientRect()
    const gap = opts.clearGap ?? 0
    if (B && B.width) {
      gl.uniform4f(U.clear, (B.left - H.left) / scale - gap, (B.top - H.top) / scale - gap, B.width / scale + gap * 2, B.height / scale + gap * 2)
      gl.uniform1f(U.clearR, (opts.clearRadius ?? 6) + gap)
      gl.uniform1f(U.clearFade, opts.clearFade ?? 0)
    } else {
      gl.uniform4f(U.clear, 0, 0, 0, 0)
    }
    buildField(scale)
    draw()
  }

  // Uniforms that change with time and parameters.
  const draw = () => {
    if (disposed || !w) return
    const P = params, m = Math.min(w, h)
    let k = [0, 0, 0, 0], a = center, b: [number, number] = [0, 0]
    switch (pattern) {
      case 'stations': {
        if (p2) { b = p2 } else {
          const s = Math.max(0, P.separation + P.breathe * 0.22 * Math.sin(t * 0.5)) * w * 0.09
          a = [center[0] - s, center[1]]; b = [center[0] + s, center[1]]
        }
        k = [P.dot, 0, 0, 0]
        break
      }
      case 'echo': k = [P.tilt + (loop ? 2.2 * Math.sin(t * 0.18) : 0), 0, 0, 0]; break
      case 'typeball': k = [P.spokes, P.offset * m, P.hub * m, t * P.speed]; break
      case 'platen': k = [P.angle + (loop ? 0.9 * Math.sin(t * 0.17) : 0), P.feed + (loop ? t * 2.4 : 0), 0, 0]; break
      case 'ribbon': k = [P.angle + (loop ? 1.4 * Math.sin(t * 0.13) : 0), 0, 0, 0]; break
      case 'swell': k = [t * 0.6, -t * 0.45, Math.max(P.amplitude * h, 6), Math.max(P.wavelength * w, 30)]; b = [t * 1.5, 0]; break
    }
    gl.uniform2f(U.p1, a[0], a[1])
    gl.uniform2f(U.p2, b[0], b[1])
    gl.uniform4f(U.k, k[0], k[1], k[2], k[3])
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }

  const frame = (now: number) => {
    raf = 0
    if (disposed) return
    const dt = last ? Math.min(0.1, (now - last) / 1000) : 0
    last = now
    if (loop) t += dt
    for (const [name, [from, to, start, dur]] of tweens) {
      const e = Math.min(1, (now - start) / dur)
      params[name] = from + (to - from) * (1 - (1 - e) * (1 - e) * (1 - e))
      if (e >= 1) tweens.delete(name)
    }
    draw()
    schedule()
  }
  const schedule = () => {
    if (raf || disposed || !visible || document.hidden) { if (!raf) last = 0; return }
    if (!loop && !tweens.size) { last = 0; return }
    raf = requestAnimationFrame(frame)
  }

  const io = new IntersectionObserver((entries) => {
    visible = entries.some((e) => e.isIntersecting)
    schedule()
  })
  io.observe(host)
  const onVisibility = () => schedule()
  document.addEventListener('visibilitychange', onVisibility)

  let rt = 0
  const later = () => { clearTimeout(rt); rt = window.setTimeout(layout, pattern === 'echo' ? 120 : 0) }
  const ro = new ResizeObserver(later)
  ro.observe(host)
  if (opts.clear) ro.observe(opts.clear)
  if (opts.mark) ro.observe(opts.mark)
  layout()

  return {
    set(next, ms = 0) {
      const now = performance.now()
      for (const [name, value] of Object.entries(next)) {
        if (ms > 0 && !reduced && w) tweens.set(name, [params[name] ?? value, value, now, ms])
        else { tweens.delete(name); params[name] = value }
      }
      draw()
      schedule()
    },
    setLoop(next) {
      loop = next && !reduced
      schedule()
    },
    setColors(colors) {
      setColors(colors)
      draw()
    },
    refresh: layout,
    dispose() {
      disposed = true
      cancelAnimationFrame(raf)
      clearTimeout(rt)
      io.disconnect()
      ro.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    },
  }
}
